// Business Category Types
export interface BusinessCategory {
    id: string;
    name: string;
    icon_url: string | null;
    hover_icon_url: string | null;
    order_number: number;
    brand_keys: string[];
    brand_names: string[];
    brand_ids: string[];
}

export interface BusinessCategoryOption {
    value: string;
    label: string;
}

// Organization (City) Types
export interface Organization {
    id: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    name: string;
    state: string;
    organization_type: string;
    location_name: string;
}

// Raw organization item from GET v1/auth/organizations
export interface OrganizationItemRawApiResponse {
    id: string;
    name: string;
    description?: string | null;
    organization_type?: string | number | null;
    state?: string | null;
    icon_url?: string | null;
    location_name: string;
    created_at: string;
    updated_at: string;
}

export const mapOrganizationItemToOrganization = (rawData: OrganizationItemRawApiResponse): Organization => {
    return {
        id: rawData.id,
        created_at: rawData.created_at,
        updated_at: rawData.updated_at,
        deleted_at: null,
        name: rawData.name ?? '',
        state: rawData.state ?? '',
        organization_type: rawData.organization_type === null || rawData.organization_type === undefined
            ? ''
            : String(rawData.organization_type),
        location_name: rawData.location_name ?? '',
    };
};

// Raw API Response Types (matching actual API)
export interface BusinessProfileRawApiResponse {
    id: string;
    logo_url: string;
    business_name: string;
    short_intro: string;
    description: string;
    business_url: string;
    business_category_id: string;
    partner_type?: string | null;
    social_links: {
        facebook: string | null;
        instagram: string | null;
        linkedin: string | null;
        tiktok: string | null;
    };
    locations: Array<{
        id: string;
        address: string;
        city: string;
        state: string;
        zip_code: string;
        latitude?: number;
        longitude?: number;
    }>;
    business_hours: Array<{
        id: string;
        day_of_week: string;
        is_open: boolean;
        open_time: string | null;
        close_time: string | null;
    }>;
}

// Logic Model (API Request/Response)
export interface BusinessProfileApiRequest {
    business_name: string;
    business_short_intro: string;
    business_category: string;
    business_url: string;
    description: string;
    logo_url?: string;
    addresses: BusinessAddressApiRequest[];
    business_hours: BusinessHoursApiRequest[];
    social_media: SocialMediaApiRequest;
}

export interface BusinessAddressApiRequest {
    address_1: string;
    city: string;
    state: string;
    zipcode: string;
    latitude?: number;
    longitude?: number;
}

export interface BusinessHoursApiRequest {
    day: string; // 'monday', 'tuesday', etc.
    is_open: boolean;
    open_time?: string; // HH:mm format
    close_time?: string; // HH:mm format
}

export interface SocialMediaApiRequest {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    tiktok?: string;
}

export interface BusinessProfileApiResponse {
    id: string;
    business_name: string;
    business_short_intro: string;
    business_category: string;
    partner_type: string;
    business_url: string;
    description: string;
    logo_url?: string;
    addresses: BusinessAddressApiResponse[];
    business_hours: BusinessHoursApiResponse[];
    social_media: SocialMediaApiResponse;
    created_at: string;
    updated_at: string;
}

export interface BusinessAddressApiResponse {
    id: string;
    address_1: string;
    city: string;
    state: string;
    zipcode: string;
    latitude?: number;
    longitude?: number;
}

export interface BusinessHoursApiResponse {
    id: string;
    day: string;
    is_open: boolean;
    open_time?: string;
    close_time?: string;
}

export interface SocialMediaApiResponse {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    tiktok?: string;
}

// UI Model (State Management)
export interface BusinessProfileUIState {
    business_name: string;
    business_short_intro: string;
    business_category: string;
    partner_type: string;
    business_url: string;
    description: string;
    logo_url?: string;
    logo_file?: File | null;
    addresses: BusinessAddressUIState[];
    business_hours: BusinessHoursUIState[];
    social_media: SocialMediaUIState;
    isEditing: boolean;
    isLoading: boolean;
    isSaving: boolean;
}

export interface BusinessAddressUIState {
    id?: string;
    address_1: string;
    city: string;
    state: string;
    zipcode: string;
    latitude?: number;
    longitude?: number;
}

export interface BusinessHoursUIState {
    id?: string;
    day: string;
    is_open: boolean;
    open_time: string; // HH:mm format
    close_time: string; // HH:mm format
}

export interface SocialMediaUIState {
    facebook: string;
    instagram: string;
    linkedin: string;
    tiktok: string;
}

// Data Mapper
export const mapApiResponseToUI = (apiData: BusinessProfileApiResponse): BusinessProfileUIState => {
    // Ensure we have all week days
    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const hoursMap = new Map(apiData.business_hours.map(h => [h.day, h]));

    const business_hours = weekDays.map(day => {
        const existing = hoursMap.get(day);
        return existing ? {
            id: existing.id,
            day: existing.day,
            is_open: existing.is_open,
            open_time: existing.open_time || '',
            close_time: existing.close_time || '',
        } : {
            day,
            is_open: false,
            open_time: '',
            close_time: '',
        };
    });

    return {
        business_name: apiData.business_name || '',
        business_short_intro: apiData.business_short_intro || '',
        business_category: apiData.business_category || '',
        partner_type: apiData.partner_type || '',
        business_url: apiData.business_url || '',
        description: apiData.description || '',
        logo_url: apiData.logo_url,
        addresses: apiData.addresses.length > 0
            ? apiData.addresses.map(addr => ({
                id: addr.id,
                address_1: addr.address_1 || '',
                city: addr.city || '',
                state: addr.state || '',
                zipcode: addr.zipcode || '',
                latitude: addr.latitude,
                longitude: addr.longitude,
            }))
            : [{
                address_1: '',
                city: '',
                state: '',
                zipcode: '',
                latitude: undefined,
                longitude: undefined,
            }],
        business_hours,
        social_media: {
            facebook: apiData.social_media?.facebook || '',
            instagram: apiData.social_media?.instagram || '',
            linkedin: apiData.social_media?.linkedin || '',
            tiktok: apiData.social_media?.tiktok || '',
        },
        isEditing: false,
        isLoading: false,
        isSaving: false,
    };
};

export const mapUIToApiRequest = (uiData: BusinessProfileUIState): BusinessProfileApiRequest => {
    return {
        business_name: uiData.business_name,
        business_short_intro: uiData.business_short_intro,
        business_category: uiData.business_category,
        business_url: uiData.business_url,
        description: uiData.description,
        logo_url: uiData.logo_url,
        addresses: uiData.addresses.map(addr => ({
            address_1: addr.address_1,
            city: addr.city,
            state: addr.state,
            zipcode: addr.zipcode,
            latitude: addr.latitude,
            longitude: addr.longitude,
        })),
        business_hours: uiData.business_hours.map(hour => ({
            day: hour.day,
            is_open: hour.is_open,
            open_time: hour.is_open ? hour.open_time : undefined,
            close_time: hour.is_open ? hour.close_time : undefined,
        })),
        social_media: {
            facebook: uiData.social_media.facebook || undefined,
            instagram: uiData.social_media.instagram || undefined,
            linkedin: uiData.social_media.linkedin || undefined,
            tiktok: uiData.social_media.tiktok || undefined,
        },
    };
};

// API Update Request Types (matching actual PUT API)
export interface BusinessProfileUpdateRequest {
    business_name: string;
    short_intro: string;
    description: string;
    business_url: string;
    business_category_id: string;
    partner_type: string | null;
    social_links: {
        facebook: string | null;
        instagram: string | null;
        linkedin: string | null;
        tiktok: string | null;
    };
    locations: Array<{
        id?: string;
        address: string;
        city: string;
        state: string;
        zip_code: string;
        latitude?: number;
        longitude?: number;
    }>;
    business_hours: Array<{
        day_of_week: string;
        is_open: boolean;
        open_time: string | null;
        close_time: string | null;
    }>;
}

// Mapper from UI State to API Update Request
export const mapUIToUpdateRequest = (uiData: BusinessProfileUIState): BusinessProfileUpdateRequest => {
    // Helper function to convert empty string to null
    const toNull = (value: string | undefined): string | null => {
        return value && value.trim() !== '' ? value : null;
    };

    return {
        business_name: uiData.business_name || '',
        short_intro: uiData.business_short_intro || '',
        description: uiData.description || '',
        business_url: uiData.business_url || '',
        business_category_id: uiData.business_category || '',
        partner_type: toNull(uiData.partner_type),
        social_links: {
            facebook: toNull(uiData.social_media.facebook),
            instagram: toNull(uiData.social_media.instagram),
            linkedin: toNull(uiData.social_media.linkedin),
            tiktok: toNull(uiData.social_media.tiktok),
        },
        locations: uiData.addresses.map(addr => ({
            id: addr.id,
            address: addr.address_1 || '',
            city: addr.city || '',
            state: addr.state || '',
            zip_code: addr.zipcode || '',
            latitude: addr.latitude,
            longitude: addr.longitude,
        })),
        business_hours: uiData.business_hours.map(hour => ({
            day_of_week: hour.day,
            is_open: hour.is_open,
            open_time: hour.is_open && hour.open_time ? hour.open_time : null,
            close_time: hour.is_open && hour.close_time ? hour.close_time : null,
        })),
    };
};

// Mapper from Raw API Response to BusinessProfileApiResponse
export const mapRawApiResponseToApiResponse = (rawData: BusinessProfileRawApiResponse): BusinessProfileApiResponse => {
    return {
        id: rawData.id,
        business_name: rawData.business_name || '',
        business_short_intro: rawData.short_intro || '',
        business_category: rawData.business_category_id || '',
        partner_type: rawData.partner_type || '',
        business_url: rawData.business_url || '',
        description: rawData.description || '',
        logo_url: rawData.logo_url || undefined,
        addresses: rawData.locations?.map(location => ({
            id: location.id,
            address_1: location.address || '',
            city: location.city || '',
            state: location.state || '',
            zipcode: location.zip_code || '',
            latitude: location.latitude,
            longitude: location.longitude,
        })) || [],
        business_hours: rawData.business_hours?.map(hour => ({
            id: hour.id,
            day: hour.day_of_week || '',
            is_open: hour.is_open,
            open_time: hour.open_time || undefined,
            close_time: hour.close_time || undefined,
        })) || [],
        social_media: {
            facebook: rawData.social_links?.facebook || undefined,
            instagram: rawData.social_links?.instagram || undefined,
            linkedin: rawData.social_links?.linkedin || undefined,
            tiktok: rawData.social_links?.tiktok || undefined,
        },
        created_at: '', // Not provided in API response
        updated_at: '', // Not provided in API response
    };
};
